import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Table,
  Typography,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getAccountDummyMembers,
  getEditableMode,
  getSelectedAccount,
  getSelectedAccountMembers,
  getSelectedProject,
  setAccountDummyMembers,
  getDefaultDummyMember,
  setDummyProjects,
  getAccountDummyProjects,
  toggleEditableMode,
  setGroupBy,
  resetDummyProjects,
  setEditableForMembers,
  GroupAccountMembersBy,
  getSelectedAccountProject,
  getEditablePingId,
  calculateMargin,
} from '../../redux/account';
import { userDataSelectors } from '../../redux/userData';
import { BottomPanel } from '../../components';
import { GroupedMembers } from '../../types';
import { CsvDownloadButton } from './DownloadCsv/DownloadCsv';
import { useTitle } from '../../hooks/useTitle';
import { PageTitle } from '../../utils/pageTitles';
import { TeamMemberViewHead } from '../teamMemberView/TeamMemberViewHead';
import { TeamListMemberView } from '../teamMemberView/TeamListMemberView';

const StyledBudgetStack = styled(Stack)`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${({ theme }) => theme.spacing(1)};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: 0;
`;

const StyledBudgetTable = styled(Table)`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 0 ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(1)};
`;

export const MarginDetails = () => {
  const dispatch = useAppDispatch();
  const [openAddDummyMemberDialog, setOpenAddDummyMemberDialog] =
    useState(false);
  const { projectId } = useParams();
  const account = useAppSelector(getSelectedAccount);
  const project = useAppSelector(getSelectedProject);
  const accountMembers = useAppSelector(getSelectedAccountMembers);
  const accountDummyProjects = useAppSelector(getAccountDummyProjects);
  const accountDummyMembers = useAppSelector(getAccountDummyMembers);
  const user = useAppSelector(userDataSelectors.getUserData);
  const hasAccounts = Boolean(user?.permissions?.hasAccounts);
  const setPageTitle = useTitle();

  const editableMode = useAppSelector(getEditableMode);
  const editablePingId = useAppSelector(getEditablePingId);

  useEffect(() => {
    setPageTitle(
      `${account?.name ?? PageTitle.portfolio} | ${PageTitle.ratesAndMargin}`,
    );
  }, [setPageTitle, account]);

  useEffect(() => {
    if (!editablePingId || !editableMode) {
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(calculateMargin()); // Explicitly pass empty object to match thunk parameter type
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [dispatch, editablePingId, editableMode]);

  const addDummyTeam = useCallback(() => {
    dispatch(setDummyProjects());
  }, [dispatch]);

  const isProjectView = useMemo(
    () => Boolean(projectId && project),
    [projectId, project],
  );

  const addDummyTeamMember = useCallback(() => {
    if (isProjectView && projectId)
      dispatch(setAccountDummyMembers(getDefaultDummyMember(projectId)));
    else {
      setOpenAddDummyMemberDialog(true);
    }
  }, [dispatch, isProjectView, projectId]);

  return (
    <>
      {account && (
        <>
          <StyledBudgetStack>
            <StyledBudgetTable stickyHeader>
              <TeamMemberViewHead />
              <TeamListMemberView
                dummyMembers={accountDummyMembers}
                dummyProjects={accountDummyProjects}
                groupedMembers={accountMembers}
                grossMarginHighlights={account.grossMarginHighlights}
                netMarginHighlights={account.netMarginHighlights}
                hasAccounts={hasAccounts}
                isProjectView={isProjectView}
                isEditableMode={Boolean(editableMode)}
                accountName={account.name}
              />
            </StyledBudgetTable>
          </StyledBudgetStack>
          {openAddDummyMemberDialog && (
            <AddDummyMember
              isOpen={openAddDummyMemberDialog}
              onClose={() => setOpenAddDummyMemberDialog(false)}
              groupedMembers={accountMembers}
            />
          )}
          {hasAccounts && (
            <MarginDetailsFooter
              isProjectView={isProjectView}
              onAddDummyMember={addDummyTeamMember}
              onAddDummyTeam={addDummyTeam}
            />
          )}
        </>
      )}
    </>
  );
};

interface BudgetsFooterProps {
  onAddDummyMember?: () => void;
  onAddDummyTeam?: () => void;
  isProjectView?: boolean;
}

const MarginDetailsFooter = ({
  onAddDummyMember,
  onAddDummyTeam,
  isProjectView,
}: BudgetsFooterProps) => {
  const editableMode = useAppSelector(getEditableMode);
  const dispatch = useAppDispatch();

  const handlePlanBudget = useCallback(
    (event: 'reset-all' | 'exit' | React.MouseEvent<HTMLButtonElement>) => {
      if (event === 'reset-all') {
        dispatch(setAccountDummyMembers());
        dispatch(resetDummyProjects());
      }

      if (event === 'exit') {
        dispatch(toggleEditableMode());
        dispatch(setGroupBy(GroupAccountMembersBy.Team));
      }

      dispatch(setEditableForMembers());
    },
    [dispatch],
  );

  if (!editableMode) {
    return <></>;
  }

  return (
    <BottomPanel
      color={BottomPanel.color.Info}
      align={BottomPanel.align.SpaceBetween}
    >
      <Box ml={6} display="flex" justifyContent="center">
        <Button
          onClick={onAddDummyMember}
          startIcon={<PersonAddIcon />}
          color="inherit"
        >
          Add a team member
        </Button>
        {!isProjectView && (
          <Button
            onClick={onAddDummyTeam}
            startIcon={<GroupAddIcon />}
            color="inherit"
          >
            Add a team
          </Button>
        )}
      </Box>
      <Box
        mr={6}
        display="flex"
        justifyContent="center"
        alignItems="baseline"
        gap={2}
      >
        <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
          This is a playground calculation, no changes will be saved
        </Typography>
        <CsvDownloadButton />
        <Button color="inherit" onClick={() => handlePlanBudget('reset-all')}>
          Reset
        </Button>
        <Button
          variant="contained"
          className="contrast"
          onClick={() => handlePlanBudget('exit')}
        >
          Exit
        </Button>
      </Box>
    </BottomPanel>
  );
};

interface AddDummyMemberProps {
  isOpen: boolean;
  onClose: () => void;
  groupedMembers: GroupedMembers[];
}

const AddDummyMember: React.FC<AddDummyMemberProps> = ({ isOpen, onClose }) => {
  const projects = useAppSelector(getSelectedAccountProject);
  const dummyProjects = useAppSelector(getAccountDummyProjects);
  const [radioValue, setradioValue] = useState('');
  const dispatch = useAppDispatch();

  const handleAddDummyMember = useCallback(
    (projectId: string) => {
      dispatch(setAccountDummyMembers(getDefaultDummyMember(projectId)));
    },
    [dispatch],
  );

  return (
    <Dialog fullWidth={true} maxWidth={'xs'} open={isOpen}>
      <DialogTitle>Add a team member</DialogTitle>
      <DialogContent>
        <FormControl>
          <FormLabel id="demo-radio-buttons-group-label">Select Team</FormLabel>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            name="radio-buttons-group"
            value={radioValue}
            onChange={(e: any) => {
              setradioValue(e.target.value);
            }}
          >
            {dummyProjects?.map(({ name, id }) => (
              <FormControlLabel
                value={id}
                control={<Radio />}
                label={name}
                key={id}
              />
            ))}

            {projects?.map(({ name, id }) => (
              <FormControlLabel
                value={id}
                control={<Radio />}
                label={name}
                key={id}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', mt: 3 }}>
        <Box display={'flex'} justifyContent={'flex-end'} width={'100%'}>
          <Button sx={{ mr: 1 }} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!radioValue}
            variant="contained"
            onClick={() => {
              if (radioValue) handleAddDummyMember(radioValue);
              onClose();
            }}
          >
            Add
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
